import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  CheckCircle as CheckIcon,
  Security as SecurityIcon,
  Sync as ReconcileIcon,
  GppMaybe as FraudIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ResourceContactForm from '../../components/forms/ResourceContactForm';
import { BUSINESS } from '../../constants/business';

const corePractices = [
  {
    title: 'Segregation of Funds',
    points: [
      'Escrow and trust funds held in federally insured accounts, separate from operating funds',
      'Accounts clearly labeled as escrow or trust accounts in the institution\'s records',
      'No commingling of client funds with agency funds, ever',
      'Separate ledger maintained per transaction or client matter',
    ],
  },
  {
    title: 'Authorized Access Controls',
    points: [
      'Written list of employees authorized to sign on or transfer from escrow accounts',
      'Background checks and ongoing supervision for anyone with access to funds',
      'Dual authorization for disbursements above a defined threshold',
      'Immediate removal of access when an employee departs',
    ],
  },
  {
    title: 'Documented Disbursement Procedures',
    points: [
      'Disburse only against a fully executed settlement statement',
      'Collected funds verified before release (good funds, not just deposited funds)',
      'Positive pay or equivalent fraud-prevention service on the account where available',
      'Voided and outstanding checks investigated and resolved on a schedule',
    ],
  },
];

const reconciliationSteps = [
  {
    step: 1,
    title: 'Bank-to-Book Reconciliation',
    description: 'Compare the bank statement balance to the checkbook or accounting system balance, adjusting for outstanding items.',
    frequency: 'Monthly minimum',
  },
  {
    step: 2,
    title: 'Book-to-Ledger Reconciliation',
    description: 'Confirm the accounting system balance equals the sum of every individual transaction ledger balance.',
    frequency: 'Monthly minimum',
  },
  {
    step: 3,
    title: 'Three-Way Match',
    description: 'Bank balance, book balance, and the total of all client ledgers must agree. Differences are documented and resolved promptly.',
    frequency: 'Monthly, reviewed by management',
  },
  {
    step: 4,
    title: 'Independent Review',
    description: 'Reconciliations performed or reviewed by someone who does not also disburse funds, with results retained for audit.',
    frequency: 'Each cycle',
  },
];

const wireControls = [
  'Verify wiring instructions by calling a known, independently confirmed phone number, never a number from the email containing the instructions',
  'Treat any last-minute change to wiring instructions as fraud until proven otherwise',
  'Use secure, encrypted channels for transmitting wire instructions to clients',
  'Educate buyers and sellers early in the transaction about wire fraud red flags',
  'Confirm receipt of funds with the sending party through a separate channel',
  'Carry cyber and crime insurance appropriate to transaction volumes',
];

const EscrowManagementGuide: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Escrow Account Management Guide for Title Professionals | MAD Title Agency</title>
        <meta
          name="description"
          content="Best practices for escrow account management at New Jersey title agencies: fund segregation, three-way reconciliation, disbursement controls, and wire fraud prevention."
        />
        <meta
          name="keywords"
          content="escrow account management, three-way reconciliation, ALTA Best Practices pillar 2, title agency escrow NJ, wire fraud prevention, escrow controls"
        />
      </Helmet>

      {/* Breadcrumb */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link> →{' '}
            <Link to="/resources" style={{ textDecoration: 'none', color: 'inherit' }}>Resources</Link> →{' '}
            Escrow Account Management Guide
          </Typography>
        </Container>
      </Box>

      {/* Header */}
      <Box sx={{ backgroundColor: '#0f172a', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Chip
                label="Professional Resource"
                sx={{ backgroundColor: '#f59e0b', color: 'white', mb: 2 }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}
              >
                Escrow Account Management Guide
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                Fund segregation, reconciliation, and disbursement controls for New Jersey title professionals
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                }}
              >
                <BankIcon sx={{ fontSize: 64, mb: 2, opacity: 0.8 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Client Funds Are Sacred
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Controls protect clients, underwriters, and your license
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Why it matters */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Why Escrow Controls Matter
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.7 }}>
            Escrow accounts hold other people's money. Sound controls are the foundation of ALTA Best
            Practices Pillar 2, a standing expectation of title insurance underwriters, and a condition
            of staying licensed with the New Jersey Department of Banking and Insurance. Escrow losses,
            whether from defalcation, error, or wire fraud, are the fastest way a title agency fails.
          </Typography>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              This guide summarizes widely accepted industry practice. It is not legal or accounting
              advice, and it does not replace your underwriter's escrow requirements or NJ DOBI
              regulations. Verify current requirements with your underwriter and counsel.
            </Typography>
          </Alert>
        </Box>

        {/* Core practices */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Core Account Practices
          </Typography>
          <Grid container spacing={4}>
            {corePractices.map((practice, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{ height: '100%', border: '1px solid #e2e8f0' }}>
                  <CardContent sx={{ p: 3 }}>
                    <SecurityIcon sx={{ fontSize: 40, color: '#1e3a8a', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {practice.title}
                    </Typography>
                    <List dense>
                      {practice.points.map((point, i) => (
                        <ListItem key={i} disableGutters alignItems="flex-start">
                          <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                            <CheckIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText primary={point} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Three-way reconciliation */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Three-Way Reconciliation
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
            The monthly three-way reconciliation is the single most important escrow control. It proves
            that the bank, your books, and your client ledgers all tell the same story:
          </Typography>
          {reconciliationSteps.map((step, index) => (
            <Card key={index} sx={{ mb: 3, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 4 }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={2}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        backgroundColor: '#1e3a8a',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        mx: 'auto',
                      }}
                    >
                      {step.step}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body1">{step.description}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Chip label={step.frequency} color="primary" sx={{ width: '100%' }} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
          <Paper sx={{ p: 3, backgroundColor: '#f0f9ff' }}>
            <ReconcileIcon sx={{ fontSize: 40, color: '#1e3a8a', mb: 1 }} />
            <Typography variant="body1">
              <strong>Practical tip:</strong> reconcile within 10 business days of the statement date,
              have someone other than the preparer sign off, and keep the working papers. When an
              underwriter or auditor asks, the question is never "do you reconcile" but "show me."
            </Typography>
          </Paper>
        </Box>

        {/* Wire fraud */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Wire Fraud Prevention
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={5}>
              <Card sx={{ p: 4, height: '100%', backgroundColor: '#fef2f2' }}>
                <FraudIcon sx={{ fontSize: 48, color: '#dc2626', mb: 2 }} />
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                  The Threat
                </Typography>
                <Typography variant="body1">
                  Business email compromise targeting real estate closings remains one of the most
                  costly cybercrimes in the United States. Attackers monitor transaction email threads,
                  then send altered wiring instructions at the moment funds are about to move. A single
                  diverted closing wire can exceed an agency's annual revenue.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Minimum controls every agency should run:
              </Typography>
              <List>
                {wireControls.map((control, index) => (
                  <ListItem key={index} alignItems="flex-start">
                    <ListItemIcon sx={{ mt: 0.5 }}>
                      <CheckIcon sx={{ color: '#16a34a' }} />
                    </ListItemIcon>
                    <ListItemText primary={control} />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </Box>

        {/* Record retention */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 4, fontWeight: 600, color: '#1e3a8a' }}>
            Records and Audit Readiness
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1e3a8a' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Record</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Practice</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Reconciliation working papers</TableCell>
                  <TableCell>Retain every cycle's three-way reconciliation with reviewer sign-off</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Settlement statements and disbursement ledgers</TableCell>
                  <TableCell>Retained per file, consistent with underwriter and state requirements (commonly 7+ years)</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Bank statements and cancelled checks</TableCell>
                  <TableCell>Complete, unaltered statements retained for the full retention period</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Authorized signer list</TableCell>
                  <TableCell>Reviewed and re-approved at least annually and on every staffing change</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Escrow policies and procedures manual</TableCell>
                  <TableCell>Written, dated, and updated when practices change; staff trained on it</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Alert severity="info">
            <Typography variant="body2">
              Pair this guide with our{' '}
              <Link to="/resources/alta-compliance-checklist" style={{ fontWeight: 600 }}>
                ALTA Best Practices Compliance Checklist
              </Link>{' '}
              to self-assess your full compliance posture, or email us at{' '}
              <strong>{BUSINESS.contact.email}</strong> with escrow practice questions.
            </Typography>
          </Alert>
        </Box>

        {/* Contact Form Section */}
        <ResourceContactForm
          resourceTitle="Escrow Account Management Guide"
          resourceType="professional"
          formType="consultation"
        />
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600, color: '#64748b' }}>
              Partner With a Compliant Title Agency
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
              We hold our escrow operations to these standards. Let's work together.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Contact Us
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/resources/alta-compliance-checklist"
                sx={{ borderColor: '#1e3a8a', color: '#1e3a8a', px: 4, py: 1.5, fontSize: '1.1rem' }}
              >
                ALTA Compliance Checklist
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default EscrowManagementGuide;
